import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { useToast } from '@/common/hooks/useToast'
import { useMediaQueryToken } from '@/common/hooks/useMediaQuery'
import { DataTable } from '@/common/components/data-table'
import { DataGrid } from '@/common/components/data-grid'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Badge } from '@/common/components/badge'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { DropdownMenu } from '@/common/components/dropdown-menu'
import { AlertDialog } from '@/common/components/alert-dialog'
import { sprinkles } from '@/core/styles/sprinkles.css'
import { pageContainer } from '@/core/styles'
import { usePropertiesSource } from '@/features/properties/hooks/usePropertiesSource'
import { PropertiesToolbar } from '@/features/properties/components/PropertiesToolbar'
import { MobilePropertiesToolbar } from '@/features/properties/components/PropertiesToolbar/MobilePropertiesToolbar'
import { PropertyCard } from '@/features/properties/components/PropertyCard'
import { OutcomeQueueBanner } from '@/features/properties/components/DealHubOutcomeQueue'
import { buyboxToFilters, getBuybox, type BuyboxResponse } from '@/features/buyboxes'
import { useTourContext, DemoPropertyCard } from '@/features/product-tour'
import { useAgentTrainingMode } from '@/features/agent-training-tour'
import type { PropertyResponse, PropertyStatus, PropertyType, ConditionType } from '@/features/properties/api/properties'
import { deleteProperty } from '@/features/properties/api/properties'
import { getPropertyContractStatuses } from '@/features/offers/api/contracts'
import type { PropertyFilters } from '@/features/properties/data/propertiesSource'
import {
  parseUrlFilters,
  parseUrlStatuses,
  parseUrlSort,
  updateUrlState,
  URL_PARAMS,
  DEFAULT_SORT,
  AGENT_DEFAULT_STATUSES,
} from '@/features/properties/utils/urlState'

export const Route = createFileRoute({
  component: PropertiesListPage,
})

// =============================================================================
// Constants
// =============================================================================

const statusColor: Record<PropertyStatus, 'info' | 'success' | 'caution' | 'error' | 'neutral'> = {
  available: 'success',
  draft: 'info',
  under_contract: 'caution',
  sold: 'neutral',
  removed: 'error',
}

type OfferStatus = 'pending' | 'accepted' | 'rejected' | 'withdrawn'

const offerStatusColor: Record<OfferStatus, 'info' | 'success' | 'caution' | 'error' | 'neutral'> = {
  pending: 'caution',
  accepted: 'success',
  rejected: 'error',
  withdrawn: 'neutral',
}

const offerStatusLabels: Record<OfferStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
}

const statusLabels: Record<PropertyStatus, string> = {
  available: 'Available',
  draft: 'Draft',
  under_contract: 'Under Contract',
  sold: 'Sold',
  removed: 'Removed',
}

const propertyTypeLabels: Record<PropertyType, string> = {
  sfh: 'SFH',
  multi_family: 'Multi',
  condo: 'Condo',
  townhome: 'Town',
}

const conditionLabels: Record<ConditionType, string> = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
  unknown: 'Unknown',
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

// =============================================================================
// Property Cell - Shared between both views
// =============================================================================

function PropertyCell({ property }: { property: PropertyResponse }) {
  // Use the thumbnail virtual field from the API (first photo ordered by display_order)
  const thumbnailUrl = property.thumbnail

  return (
    <Stack direction="row" gap="12" align="center">
      {/* Thumbnail */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 4,
          backgroundColor: '#f0f0f0',
          backgroundImage: thumbnailUrl ? `url("${thumbnailUrl}")` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!thumbnailUrl && <Icon name="home" size={20} style={{ color: '#999' }} />}
      </div>
      {/* Address */}
      <Stack gap="4">
        <Text weight="medium" variant="bodySm" style={{ color: '#027F87' }}>
          {property.address.street_address}
          {property.address.unit_number && ` ${property.address.unit_number}`}
        </Text>
        <Text variant="bodyXs" color="muted">
          {property.address.city}, {property.address.state} {property.address.zipcode}
        </Text>
      </Stack>
    </Stack>
  )
}

// =============================================================================
// Actions Menu - Agent view only
// =============================================================================

interface ActionsMenuProps {
  property: PropertyResponse
  onViewProperty: (property: PropertyResponse) => void
  onViewOffers: (property: PropertyResponse) => void
  onEdit: (property: PropertyResponse) => void
  onDelete: (property: PropertyResponse) => void
  isDeleting: boolean
}

function ActionsMenu({ property, onViewProperty, onViewOffers, onEdit, onDelete, isDeleting }: ActionsMenuProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Only drafts can be deleted from the list view
  const isDraft = property.status === 'draft'

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false)
    onDelete(property)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <Button variant="ghost" size="sm" aria-label="Actions" onClick={(e) => e.stopPropagation()}>
            <Icon name="more_vert" size={18} />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end">
          <DropdownMenu.Item onSelect={() => onViewProperty(property)}>
            <Icon name="visibility" size={16} />
            View property
          </DropdownMenu.Item>
          {/* Hide view offers for draft properties - they can't have offers */}
          {!isDraft && (
            <DropdownMenu.Item onSelect={() => onViewOffers(property)}>
              <Icon name="local_offer" size={16} />
              View offers
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Separator />
          <DropdownMenu.Item onSelect={() => onEdit(property)}>
            <Icon name="edit" size={16} />
            Edit
          </DropdownMenu.Item>
          {/* Only show delete option for draft properties */}
          {isDraft && (
            <>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                onSelect={() => setDeleteDialogOpen(true)}
                style={{ color: 'var(--color-persimmon)' }}
              >
                <Icon name="delete" size={16} />
                Delete
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialog.Content>
          <AlertDialog.Title>Delete Draft Property</AlertDialog.Title>
          <AlertDialog.Description>
            Are you sure you want to delete this draft property? This action is permanent and cannot be undone.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isDeleting}>Cancel</AlertDialog.Cancel>
            <AlertDialog.Action
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  )
}

// =============================================================================
// Demo Property Card (shown during product tour)
// =============================================================================

function DemoPropertyCardWrapper() {
  const { showDemoProperty } = useTourContext()
  if (!showDemoProperty) return null
  return <DemoPropertyCard />
}

// =============================================================================
// Main Component
// =============================================================================

function PropertiesListPage() {
  const { roles, token } = useAuth()
  const queryClient = useQueryClient()
  const { data: userProfile } = useUserProfile()
  const { showSuccessToast, showErrorToast } = useToast()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as Record<string, string | undefined>
  const isTabletDown = useMediaQueryToken('tabletDown')

  // State for delete operation
  const [deletingPropertyId, setDeletingPropertyId] = useState<string | null>(null)

  // Determine user role for view customization
  const isAgent = roles.includes('agent')
  const isInvestor = roles.includes('investor')
  const isAdmin = roles.includes('admin')

  // Parse URL state based on view type
  const isAgentView = isAgent || isAdmin
  const isInvestorView = isInvestor && !isAgent && !isAdmin

  // Agent view: parse statuses, search, sort from URL
  const urlStatuses = parseUrlStatuses(search[URL_PARAMS.statuses] ?? null, AGENT_DEFAULT_STATUSES)
  const urlSearch = search[URL_PARAMS.search] ?? ''
  const urlSort = parseUrlSort(search[URL_PARAMS.sort] ?? null)

  // Investor view: parse buybox ID and filter overrides from URL
  // Use undefined (not null) when no buybox in URL, so toolbar operates in uncontrolled mode
  const urlBuyboxId = search[URL_PARAMS.buybox] ?? undefined
  // Convert search object to URLSearchParams for parseUrlFilters
  const searchParams = useMemo(() => {
    const params = new URLSearchParams()
    Object.entries(search).forEach(([key, value]) => {
      if (value !== undefined) params.set(key, value)
    })
    return params
  }, [search])
  const urlFilterOverrides = parseUrlFilters(searchParams)

  // Capture initial URL values on first render only (for initializing state/source)
  // This prevents source recreation when URL changes after user interactions
  const initialUrlRef = useRef({
    statuses: urlStatuses,
    filterOverrides: urlFilterOverrides,
    sort: urlSort,
    search: urlSearch,
    buyboxId: urlBuyboxId,
  })

  // =============================================================================
  // Investor View: Pre-fetch buybox data before rendering DataTable
  // =============================================================================

  // Fetch the initial buybox by ID (investor view only)
  const [initialBuybox, setInitialBuybox] = useState<BuyboxResponse | null>(null)
  const [buyboxLoading, setBuyboxLoading] = useState(
    isInvestorView && !!initialUrlRef.current.buyboxId
  )

  // Fetch buybox by ID on mount (only if investor view with buybox in URL)
  useEffect(() => {
    const buyboxId = initialUrlRef.current.buyboxId
    if (!isInvestorView || !buyboxId || !token) {
      setBuyboxLoading(false)
      return
    }

    getBuybox(token, buyboxId)
      .then((buybox) => setInitialBuybox(buybox))
      .catch(() => {
        // Buybox not found or error - will proceed without it
      })
      .finally(() => {
        setBuyboxLoading(false)
      })
  }, [isInvestorView, token])

  // Track if we're ready to render DataTable (buybox loaded or not needed)
  const isReady = !buyboxLoading

  // Compute initial filters based on view type - only uses initial URL values + loaded buybox
  const initialFilters = useMemo<PropertyFilters>(() => {
    if (isAgentView) {
      // Agents see all their properties regardless of MLS status
      return { statuses: initialUrlRef.current.statuses, allow_on_mls: true }
    }
    // Investor: start with status='available' and offer_deadline >= today (or null)
    const baseFilters: PropertyFilters = {
      statuses: ['available'] as PropertyStatus[],
      offer_deadline_gte: 'today',
    }

    // Default filters use profile markets (investor's preferred markets)
    const profileMarkets = userProfile?.profile?.markets ?? []
    const defaultFilters: PropertyFilters = {
      markets: profileMarkets.length > 0 ? profileMarkets : undefined,
    }

    // Filter out undefined values from URL overrides so they don't overwrite buybox values
    const urlOverrides = Object.fromEntries(
      Object.entries(initialUrlRef.current.filterOverrides).filter(([, v]) => v !== undefined)
    ) as PropertyFilters

    // If buybox found, use buybox filters as base, then overlay URL overrides
    if (initialBuybox) {
      const buyboxFilters = buyboxToFilters(initialBuybox)
      return {
        ...baseFilters,
        ...buyboxFilters,
        ...urlOverrides,
      }
    }

    // No buybox, use default filters (profile markets) + URL filter overrides
    return {
      ...baseFilters,
      ...defaultFilters,
      ...urlOverrides,
    }
  }, [isAgentView, initialBuybox, userProfile?.profile?.markets])

  // Current filters state - managed locally, synced to URL via callbacks
  const [filters, setFilters] = useState<PropertyFilters>(initialFilters)

  // Track if we've done the initial sync from buybox (to avoid overriding user changes)
  const hasInitialSyncRef = useRef(false)

  // Sync filters from initialFilters ONLY on first buybox load (not on subsequent changes)
  useEffect(() => {
    // Skip if we've already done the initial sync
    if (hasInitialSyncRef.current) return
    // Only sync once we have the buybox loaded (or confirmed no buybox needed)
    if (isReady && initialBuybox) {
      setFilters(initialFilters)
      hasInitialSyncRef.current = true
    }
  }, [isReady, initialBuybox, initialFilters])

  // Track active buybox for URL diff computation (investor view)
  const [activeBuybox, setActiveBuybox] = useState<BuyboxResponse | null>(initialBuybox)

  // Update activeBuybox when initialBuybox is loaded (only once)
  useEffect(() => {
    if (initialBuybox && !hasInitialSyncRef.current) {
      setActiveBuybox(initialBuybox)
    }
  }, [initialBuybox])

  // Create sources - desktop source lets DataTable manage field selection via columns,
  // mobile source bakes in the needed fields so the first fetch includes them
  const mobileSelectFields = useMemo(() => {
    const base = ['address', 'thumbnail', 'offer_deadline', 'status']
    if (isAgentView) {
      return [...base, 'offer_count', 'questions_count']
    }
    return [...base, 'overall_condition', 'market_value', 'house_canary_valuation', 'offer_status']
  }, [isAgentView])

  // MRG-281: when an external agent is mid-training the API returns
  // hardcoded fixtures so the guided tour has data to walk through.
  const trainingMode = useAgentTrainingMode()
  const desktopSource = usePropertiesSource({ trainingMode })
  const mobileSource = usePropertiesSource({ select: mobileSelectFields, trainingMode })

  // MRG-390: contract status map for the "Contract uploaded" pill +
  // "Upload date" column on the agent / admin My Properties tab.
  // Property-level signals across all uploaders on the property's accepted
  // offer — intentionally bypasses single-party visibility (the boolean
  // reveals nothing the user can't already see via offer status).
  const contractStatusQuery = useQuery({
    queryKey: ['property-contract-status', isAgentView, token],
    queryFn: () => {
      if (!token) throw new Error('Missing auth token')
      // Omit `propertyIds` — backend returns all the caller's properties.
      return getPropertyContractStatuses(token)
    },
    enabled: Boolean(token) && isAgentView,
    // Fairly fresh — pill should reflect new uploads within a normal page
    // session without thrashing the endpoint on every focus change.
    staleTime: 60_000,
  })

  const contractStatusByPropertyId = useMemo(() => {
    const m = new Map<string, { contractUploaded: boolean; earliestUploadedAt: string | null }>()
    for (const row of contractStatusQuery.data ?? []) {
      m.set(row.propertyId, {
        contractUploaded: row.contractUploaded,
        earliestUploadedAt: row.earliestUploadedAt,
      })
    }
    return m
  }, [contractStatusQuery.data])

  // Parse sort into field/direction for DataTable - uses initial URL value
  const initialSort = useMemo(() => {
    const [field, direction] = initialUrlRef.current.sort.split(':')
    return { field, direction: direction as 'asc' | 'desc' }
  }, [])

  // Default filters for investor view (no buybox) - used for URL diff computation
  const defaultInvestorFilters = useMemo<PropertyFilters>(() => {
    const profileMarkets = userProfile?.profile?.markets ?? []
    return {
      markets: profileMarkets.length > 0 ? profileMarkets : undefined,
    }
  }, [userProfile?.profile?.markets])

  // =============================================================================
  // URL Update Callbacks
  // =============================================================================

  // Helper to update URL params
  const updateUrl = useCallback(
    (updates: Parameters<typeof updateUrlState>[1]) => {
      navigate({
        search: ((prev: Record<string, string | undefined>) => {
          const prevParams = new URLSearchParams()
          Object.entries(prev).forEach(([key, value]) => {
            if (value !== undefined) prevParams.set(key, value)
          })
          const newParams = updateUrlState(prevParams, updates, {
            sort: DEFAULT_SORT,
            statuses: AGENT_DEFAULT_STATUSES,
          })
          const result: Record<string, string | undefined> = {}
          newParams.forEach((value, key) => {
            result[key] = value
          })
          return result
        }) as never,
        replace: true,
      })
    },
    [navigate]
  )

  // Agent view: handle status filter change
  const handleAgentFiltersChange = useCallback(
    (newFilters: PropertyFilters) => {
      setFilters(newFilters)
      updateUrl({ statuses: newFilters.statuses ?? [] })
    },
    [updateUrl]
  )

  // Agent view: handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      updateUrl({ search: value })
    },
    [updateUrl]
  )

  // Investor view: handle buybox selection change
  const handleBuyboxIdChange = useCallback(
    (id: string | null, isNew: boolean) => {
      // When buybox changes, clear filter overrides from URL
      updateUrl({
        buyboxId: id,
        filters: isNew ? {} : undefined, // Clear filters when creating new
      })
    },
    [updateUrl]
  )

  // Investor view: handle filters change
  // baseBuybox is passed when selecting a buybox (for correct URL diff and buyboxId)
  // baseBuybox === null means explicitly clearing (create new)
  // baseBuybox === undefined means use activeBuybox state (modifying filters)
  const handleInvestorFiltersChange = useCallback(
    (newFilters: PropertyFilters, baseBuybox?: BuyboxResponse | null) => {
      setFilters(newFilters)

      // baseBuybox explicitly passed (selecting or clearing)
      if (baseBuybox !== undefined) {
        if (baseBuybox) {
          // Selecting a buybox - set buyboxId, compute filter diff
          const baseFilters = buyboxToFilters(baseBuybox)
          updateUrl({ filters: newFilters, baseFilters, buyboxId: baseBuybox.id })
        } else {
          // Creating new (null) - clear buyboxId, compare against default filters
          // so markets aren't written to URL if they match profile defaults
          updateUrl({ filters: newFilters, baseFilters: defaultInvestorFilters, buyboxId: null })
        }
        return
      }

      // baseBuybox not passed - use activeBuybox state (modifying existing filters)
      if (activeBuybox) {
        const baseFilters = buyboxToFilters(activeBuybox)
        updateUrl({ filters: newFilters, baseFilters })
      } else {
        // No buybox selected - compare against default investor filters (profile markets)
        // This way, if filters match defaults, they won't be written to URL
        updateUrl({ filters: newFilters, baseFilters: defaultInvestorFilters })
      }
    },
    [updateUrl, activeBuybox, defaultInvestorFilters]
  )

  // Investor view: handle active buybox data change (for diff computation)
  const handleActiveBuyboxChange = useCallback(
    (buybox: BuyboxResponse | null) => {
      setActiveBuybox(buybox)
    },
    []
  )

  // Handle sort change (both views)
  const handleSortChange = useCallback(
    (sort: { field: string; direction: 'asc' | 'desc' }) => {
      updateUrl({ sort: `${sort.field}:${sort.direction}` })
    },
    [updateUrl]
  )

  // Page title based on role
  const pageTitle = isAgent || isAdmin ? 'My properties' : 'Browse properties'

  // Handlers for actions menu
  const handleViewProperty = (property: PropertyResponse) => {
    navigate({ to: '/properties/$id', params: { id: property.id } })
  }

  const handleViewOffers = (property: PropertyResponse) => {
    navigate({ to: '/properties/$id/offers', params: { id: property.id }, search: { offerId: undefined } })
  }

  const handleEdit = (property: PropertyResponse) => {
    navigate({ to: '/properties/$id/edit', params: { id: property.id } })
  }

  const handleDelete = async (property: PropertyResponse) => {
    if (!token) return

    setDeletingPropertyId(property.id)
    try {
      await deleteProperty(token, property.id)
      showSuccessToast('Property deleted successfully')
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['dataSource', 'properties'] })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete property'
      showErrorToast(message)
    } finally {
      setDeletingPropertyId(null)
    }
  }

  // Show loading state while waiting for buybox to load (investor view)
  if (!isReady) {
    return (
      <Stack gap="24" className={pageContainer({ width: 'full' })} style={{ height: '100%' }}>
        {/* Page Header */}
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">
            {pageTitle}
          </Text>
          <Text variant="bodySm" color="muted">
            Browse available properties from our network of agents.
          </Text>
        </Stack>
        {/* Loading state */}
        <Stack align="center" justify="center" style={{ flex: 1 }}>
          <Text color="muted">Loading...</Text>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack gap="24" className={pageContainer({ width: 'full' })}>
      {/* Page Header */}
      <Stack gap="4">
        <Text as="h1" variant="h4" weight="bold">
          {pageTitle}
        </Text>
        <Text
          variant="bodySm"
          color="muted"
          className={sprinkles({ display: { base: 'block', tabletDown: 'none' } })}
        >
          {isAgent || isAdmin
            ? 'Properties you have submitted to Orchard Offers to receive cash offers from the investor network.'
            : 'Browse available properties from our network of agents.'}
        </Text>
      </Stack>

      {/*
        Standing entry point into the Deal Hub outcome queue. Renders nothing
        unless something is actually waiting, so it costs the page nothing on
        the ordinary day.
      */}
      {(isAgent || isAdmin) && <OutcomeQueueBanner />}

      {/* Properties list - DataTable on desktop, DataGrid cards on mobile */}
      <div style={{ flex: 1, minHeight: 0 }} data-tour-step="agent-listings-table">
        {isTabletDown ? (
          <DataGrid
            source={mobileSource}
            getItemKey={(p) => p.id}
            initialSort={{ field: 'offer_deadline', direction: 'desc' }}
            initialFilters={initialFilters as Record<string, unknown>}
            initialSearch={initialUrlRef.current.search}
            columns={1}
            gap="12"
            emptyMessage="No properties found"
            renderItem={(property) => (
              <PropertyCard
                property={property}
                variant={isAgentView ? 'agent' : 'investor'}
                onClick={(p) => navigate({ to: '/properties/$id', params: { id: p.id } })}
              />
            )}
          >
            <DataGrid.Toolbar>
              <MobilePropertiesToolbar
                filters={filters}
                onFiltersChange={isAgentView ? handleAgentFiltersChange : handleInvestorFiltersChange}
                search={urlSearch}
                onSearchChange={handleSearchChange}
                buyboxId={urlBuyboxId}
                onBuyboxIdChange={handleBuyboxIdChange}
                onActiveBuyboxChange={handleActiveBuyboxChange}
              />
            </DataGrid.Toolbar>
          </DataGrid>
        ) : (
          <DataTable
            source={desktopSource}
            getRowKey={(p) => p.id}
            aria-label="Properties list"
            getRowLabel={(p) => p.address.full_address ?? p.address.street_address}
            initialSort={initialSort}
            initialFilters={initialFilters as Record<string, unknown>}
            initialSearch={initialUrlRef.current.search}
            additionalFields={isInvestorView ? ['thumbnail', 'offer_status', 'house_canary_valuation'] : ['thumbnail']}
            hoverable
            stickyHeader
            prependRows={<DemoPropertyCardWrapper />}
            onRowClick={(property, _index, event) => {
              const url = `/properties/${property.id}`
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
                window.open(url, '_blank')
                return
              }
              navigate({ to: '/properties/$id', params: { id: property.id } })
            }}
            onSortChange={handleSortChange}
          >
            <DataTable.Toolbar>
              <PropertiesToolbar
                filters={filters}
                onFiltersChange={isAgentView ? handleAgentFiltersChange : handleInvestorFiltersChange}
                search={urlSearch}
                onSearchChange={handleSearchChange}
                buyboxId={urlBuyboxId}
                onBuyboxIdChange={handleBuyboxIdChange}
                onActiveBuyboxChange={handleActiveBuyboxChange}
              />
            </DataTable.Toolbar>

            <DataTable.Columns>
              {/* Property - Shared */}
              <DataTable.Column<PropertyResponse>
                id="address"
                header="Property"
                width={250}
              >
                {(item) => <PropertyCell property={item} />}
              </DataTable.Column>

              {/* Client - Agent only */}
              {(isAgent || isAdmin) && (
                <DataTable.Column<PropertyResponse>
                  id="client"
                  header="Client"
                  width={120}
                  align="center"
                  sortable
                >
                  {(item) =>
                    item.client ? (
                      <Text variant="bodySm">{item.client}</Text>
                    ) : (
                      <Text variant="bodySm" color="muted">
                        —
                      </Text>
                    )
                  }
                </DataTable.Column>
              )}

              {/* Type - Investor only */}
              {isInvestorView && (
                <DataTable.Column<PropertyResponse>
                  id="property_type"
                  header="Type"
                  width={70}
                  align="center"
                >
                  {(item) => (
                    <Text variant="bodySm">{propertyTypeLabels[item.property_type]}</Text>
                  )}
                </DataTable.Column>
              )}

              {/* Condition - Investor only */}
              {isInvestorView && (
                <DataTable.Column<PropertyResponse>
                  id="overall_condition"
                  header="Condition"
                  width={90}
                  align="center"
                >
                  {(item) =>
                    item.overall_condition ? (
                      <Text variant="bodySm">{conditionLabels[item.overall_condition]}</Text>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }
                </DataTable.Column>
              )}

              {/* Beds - Investor only */}
              {isInvestorView && (
                <DataTable.Column<PropertyResponse>
                  id="beds"
                  header="Beds"
                  width={60}
                  align="center"
                >
                  {(item) =>
                    item.beds != null ? (
                      <Text variant="bodySm">{item.beds}</Text>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }
                </DataTable.Column>
              )}

              {/* Baths - Investor only */}
              {isInvestorView && (
                <DataTable.Column<PropertyResponse>
                  id="baths"
                  header="Baths"
                  width={60}
                  align="center"
                >
                  {(item) =>
                    item.baths != null ? (
                      <Text variant="bodySm">{item.baths}</Text>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }
                </DataTable.Column>
              )}

              {/* Sqft - Investor only */}
              {isInvestorView && (
                <DataTable.Column<PropertyResponse>
                  id="sqft"
                  header="Sqft"
                  width={80}
                  align="right"
                >
                  {(item) =>
                    item.sqft != null ? (
                      <Text variant="bodySm">{formatNumber(item.sqft)}</Text>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }
                </DataTable.Column>
              )}

              {/* Year Built - Investor only */}
              {isInvestorView && (
                <DataTable.Column<PropertyResponse>
                  id="year_built"
                  header="Year"
                  width={60}
                  align="center"
                >
                  {(item) =>
                    item.year_built != null ? (
                      <Text variant="bodySm">{item.year_built}</Text>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }
                </DataTable.Column>
              )}

              {/* Recommended List Price - Shared (null when investor lacks access) */}
              <DataTable.Column<PropertyResponse>
                id="market_value"
                header="Rec. list price"
                width={120}
                align="right"
                sortable
              >
                {(item) =>
                  item.market_value != null ? (
                    <Text variant="bodySm" weight="medium">
                      {formatCurrency(Number(item.market_value))}
                    </Text>
                  ) : (
                    <Text variant="bodySm" color="muted">
                      —
                    </Text>
                  )
                }
              </DataTable.Column>

              {/* AVM (House Canary Valuation) - Investor only */}
              {isInvestorView && (
                <DataTable.Column<PropertyResponse>
                  id="house_canary_valuation"
                  header="AVM"
                  width={100}
                  align="right"
                  sortable
                >
                  {(item) =>
                    item.house_canary_valuation != null ? (
                      <Text variant="bodySm" weight="medium">
                        {formatCurrency(Number(item.house_canary_valuation))}
                      </Text>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }
                </DataTable.Column>
              )}

              {/* Offers - Agent only (not sortable - virtual field) */}
              {(isAgent || isAdmin) && (
                <DataTable.Column<PropertyResponse>
                  id="offers"
                  header="Offers"
                  field="offer_count"
                  width={80}
                  align="center"
                >
                  {(item) => (
                    <Badge color={item.offer_count ? 'info' : 'neutral'}>
                      {item.offer_count ?? 0}
                    </Badge>
                  )}
                </DataTable.Column>
              )}

              {/* Questions - Agent only (count of information requests) */}
              {(isAgent || isAdmin) && (
                <DataTable.Column<PropertyResponse>
                  id="questions"
                  header="Questions"
                  field="questions_count"
                  width={100}
                  align="center"
                >
                  {(item) => (
                    <Badge color={item.questions_count ? 'info' : 'neutral'}>
                      {item.questions_count ?? 0}
                    </Badge>
                  )}
                </DataTable.Column>
              )}

              {/* Status - Agent/Admin only (investors always see 'available') */}
              {(isAgent || isAdmin) && (
                <DataTable.Column<PropertyResponse>
                  id="status"
                  header={<span data-tour-step="agent-listings-status">Status</span>}
                  width={130}
                  align="center"
                >
                  {(item) => (
                    <Badge color={statusColor[item.status]}>
                      {statusLabels[item.status]}
                    </Badge>
                  )}
                </DataTable.Column>
              )}

              {/* MRG-390: "Contract uploaded" pill + "Upload date" - Agent/Admin only.
               * AC-G1 + AC-G2. Driven by the property-level contract status map
               * fetched in parallel via getPropertyContractStatuses. */}
              {(isAgent || isAdmin) && (
                <DataTable.Column<PropertyResponse>
                  id="contract_uploaded"
                  header="Contract"
                  width={120}
                  align="center"
                  field={false}
                >
                  {(item) => {
                    const status = contractStatusByPropertyId.get(item.id)
                    return status?.contractUploaded ? (
                      <Badge color="success">Uploaded</Badge>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }}
                </DataTable.Column>
              )}

              {(isAgent || isAdmin) && (
                <DataTable.Column<PropertyResponse>
                  id="upload_date"
                  header="Upload date"
                  width={120}
                  align="center"
                  field={false}
                >
                  {(item) => {
                    const status = contractStatusByPropertyId.get(item.id)
                    return status?.earliestUploadedAt ? (
                      <Text variant="bodySm">{formatDate(status.earliestUploadedAt)}</Text>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }}
                </DataTable.Column>
              )}

              {/* Offer Deadline - Shared */}
              <DataTable.Column<PropertyResponse>
                id="offer_deadline"
                header="Offer deadline"
                width={130}
                align="center"
                sortable
              >
                {(item) =>
                  item.offer_deadline ? (
                    <Text variant="bodySm">{formatDate(item.offer_deadline)}</Text>
                  ) : (
                    <Text variant="bodySm" color="muted">
                      No deadline
                    </Text>
                  )
                }
              </DataTable.Column>

              {/* Offer Status - Investor only */}
              {isInvestorView && (
                <DataTable.Column<PropertyResponse>
                  id="offer_status"
                  header="Offer Status"
                  field="offer_status"
                  width={110}
                  align="center"
                >
                  {(item) =>
                    item.offer_status ? (
                      <Badge color={offerStatusColor[item.offer_status]}>
                        {offerStatusLabels[item.offer_status]}
                      </Badge>
                    ) : (
                      <Text variant="bodySm" color="muted">—</Text>
                    )
                  }
                </DataTable.Column>
              )}

              {/* Date Submitted - Shared */}
              <DataTable.Column<PropertyResponse>
                id="submitted_at"
                header="Submitted"
                width={120}
                align="center"
                sortable
              >
                {(item) =>
                  item.submitted_at ? (
                    <Text variant="bodySm">{formatDate(item.submitted_at)}</Text>
                  ) : (
                    <Text variant="bodySm" color="muted">
                      —
                    </Text>
                  )
                }
              </DataTable.Column>

              {/* Actions - Agent only */}
              {(isAgent || isAdmin) && (
                <DataTable.Column<PropertyResponse>
                  id="actions"
                  header=""
                  field={false}
                  width={50}
                  align="center"
                >
                  {(item) => (
                    <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                      <ActionsMenu
                        property={item}
                        onViewProperty={handleViewProperty}
                        onViewOffers={handleViewOffers}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        isDeleting={deletingPropertyId === item.id}
                      />
                    </div>
                  )}
                </DataTable.Column>
              )}
            </DataTable.Columns>
          </DataTable>
        )}
      </div>
    </Stack>
  )
}
